import { NavConfigRefocusConfigAny, NavConfigRefocusTargetAny } from '@pressreader/navigation-types';
import { WithHash } from '@pressreader/types';

import {
    areRefocusConfigsEqual,
    areRefocusTargetsEqual,
    extendRefocusConfigWithHash,
    extendRefocusTargetWithHash,
    isRefocusTargetMatchRefocusConfig,
} from '../utils';

const getActiveRefocusTarget = <T extends WithHash<NavConfigRefocusTargetAny>>(
    refocusTargets: T[],
    refocusConfigurationToMatchWith: WithHash<NavConfigRefocusConfigAny> | null,
): T | null => {
    if (!refocusConfigurationToMatchWith) {
        return null;
    }

    const targetWithHighestPriority: T | undefined = refocusTargets.find(refocusTarget => {
        return isRefocusTargetMatchRefocusConfig(refocusTarget, refocusConfigurationToMatchWith);
    });

    return targetWithHighestPriority ?? null;
};
const sortRefocusConfigurationsInDescOrder = <T extends WithHash<NavConfigRefocusConfigAny>[]>(refocusConfigs: T): T => {
    return refocusConfigs.sort((refocusConfigA, refocusConfigB) => refocusConfigB.priority - refocusConfigA.priority);
};
const getRefocusConfigurationWithHighestPriority = <C extends WithHash<NavConfigRefocusConfigAny>>(
    refocusConfigsOrderedByHighestPriorityDesc: C[],
): C | undefined => {
    return sortRefocusConfigurationsInDescOrder(refocusConfigsOrderedByHighestPriorityDesc)[0] ?? null;
};
export const chooseActiveRefocusTargetAndConfiguration = <
    T extends WithHash<NavConfigRefocusTargetAny>,
    C extends WithHash<NavConfigRefocusConfigAny>,
>(
    refocusTargets: T[],
    refocusConfigsOrderedByHighestPriorityDesc: C[],
): {
    refocusTargetActive: T | null;
    refocusConfigActive: C | null;
} => {
    const refocusConfigActive: C | null = getRefocusConfigurationWithHighestPriority(refocusConfigsOrderedByHighestPriorityDesc) ?? null;
    const refocusTargetActive: T | null = getActiveRefocusTarget(refocusTargets, refocusConfigActive);

    return {
        refocusTargetActive,
        refocusConfigActive,
    };
};

export const updateRefocusConfigurationsAndChooseActiveTarget = ({
    currentRefocusConfigs,
    newRefocusConfigurations,
    currentRefocusTargets,
}: {
    currentRefocusConfigs: WithHash<NavConfigRefocusConfigAny>[];
    newRefocusConfigurations: NavConfigRefocusConfigAny[];
    currentRefocusTargets: WithHash<NavConfigRefocusTargetAny>[];
}): {
    refocusTargetActive: WithHash<NavConfigRefocusTargetAny> | null;
    refocusConfigActive: WithHash<NavConfigRefocusConfigAny> | null;
    refocusConfigs: WithHash<NavConfigRefocusConfigAny>[];
} => {
    const newRefocusConfigsWithHash = newRefocusConfigurations.map(extendRefocusConfigWithHash);
    const currentRefocusConfigsUpdated = currentRefocusConfigs.map(currentRefocusConfig => {
        const updatedRefocusConfig = newRefocusConfigsWithHash.find(newRefocusConfig =>
            areRefocusConfigsEqual(currentRefocusConfig, newRefocusConfig),
        );

        return updatedRefocusConfig ?? currentRefocusConfig;
    });
    const newRefocusConfigsToAdd = newRefocusConfigsWithHash.filter(
        newRefocusConfig =>
            !currentRefocusConfigsUpdated.some(currentRefocusConfig => areRefocusConfigsEqual(currentRefocusConfig, newRefocusConfig)),
    );
    const updatedRefocusConfigsUpdatedOrderedByHighestPriority = sortRefocusConfigurationsInDescOrder([
        ...currentRefocusConfigsUpdated,
        ...newRefocusConfigsToAdd,
    ]);
    const { refocusConfigActive, refocusTargetActive } = chooseActiveRefocusTargetAndConfiguration(
        currentRefocusTargets,
        updatedRefocusConfigsUpdatedOrderedByHighestPriority,
    );

    return {
        refocusTargetActive,
        refocusConfigActive,
        refocusConfigs: updatedRefocusConfigsUpdatedOrderedByHighestPriority,
    };
};

export const updateRefocusTargetsAndGetActiveTarget = ({
    currentRefocusConfigs,
    newRefocusTargets,
    currentRefocusTargets,
}: {
    currentRefocusConfigs: WithHash<NavConfigRefocusConfigAny>[];
    newRefocusTargets: NavConfigRefocusTargetAny[];
    currentRefocusTargets: WithHash<NavConfigRefocusTargetAny>[];
}): {
    refocusTargetActive: WithHash<NavConfigRefocusTargetAny> | null;
    refocusConfigActive: WithHash<NavConfigRefocusConfigAny> | null;
    refocusConfigs: WithHash<NavConfigRefocusConfigAny>[];
    refocusTargets: WithHash<NavConfigRefocusTargetAny>[];
} => {
    const newRefocusTargetsWithHash = newRefocusTargets.map(extendRefocusTargetWithHash);
    const currentRefocusTargetsUpdated = currentRefocusTargets.map(currentRefocusTarget => {
        const updatedRefocusTarget = newRefocusTargetsWithHash.find(newRefocusTarget =>
            areRefocusTargetsEqual(currentRefocusTarget, newRefocusTarget),
        );

        return updatedRefocusTarget ?? currentRefocusTarget;
    });
    const newRefocusTargetsToAdd = newRefocusTargetsWithHash.filter(
        newRefocusTarget =>
            !currentRefocusTargetsUpdated.some(currentRefocusTarget => areRefocusTargetsEqual(currentRefocusTarget, newRefocusTarget)),
    );
    const updatedRefocusTargets = [...currentRefocusTargetsUpdated, ...newRefocusTargetsToAdd];

    const { refocusConfigActive, refocusTargetActive } = chooseActiveRefocusTargetAndConfiguration(updatedRefocusTargets, currentRefocusConfigs);

    return {
        refocusTargetActive,
        refocusConfigActive,
        refocusConfigs: currentRefocusConfigs,
        refocusTargets: updatedRefocusTargets,
    };
};
